import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Input, Switch, Checkbox, Collapse, Modal, Button } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import {
    SearchOutlined as Search,
    CloseOutlined as X,
    DownOutlined as ChevronDown,
    RightOutlined as ChevronRight,
} from '@ant-design/icons';
import _ from 'lodash';
import { IRequirement, ICustomRequirements, QuoteProps } from '@partsbadger/types';
import { QuestionMark } from '@partsbadger/ui-library';
import { RenderExtraInputs } from './RenderExtraInputs';

const { Panel } = Collapse;

interface AdditionalRequirementsSelectorProps {
    title: React.ReactNode;
    requirements: IRequirement[];
    defaultSelectedIds: Array<number>;
    onChange: (values: Array<ICustomRequirements>) => void;
    allowExpediteFilter?: boolean;
    expandLabel?: string;
    expandSubLabel?: string;
    customRequirements: Array<ICustomRequirements>;
}

const getRequirementsStorageKey = (quote?: QuoteProps) =>
    `quote-additional-requirements:${quote?.id ?? 'draft'}`;

/** Skip ITAR/EAR 99 confirmation per requirement when set (browser-local only). */
const EXPORT_CONTROL_REQ_ACK_STORAGE_KEY_PREFIX = 'customer-portal:getquote:export-control-requirement-ack';

const getExportControlReqAckStorageKey = (option: IRequirement): string => {
    const normalized = option.name.trim().replace(/\s+/g, '-').toLowerCase();
    return `${EXPORT_CONTROL_REQ_ACK_STORAGE_KEY_PREFIX}:${normalized}`;
};

const isExportControlRequirement = (option: IRequirement): boolean => {
    const name = option.name.trim();
    if (name === 'ITAR') return true;
    const collapsed = name.replace(/\s+/g, ' ').toUpperCase();
    return collapsed === 'EAR 99' || collapsed === 'EAR99';
};

const shouldSkipExportControlModal = (option: IRequirement): boolean => {
    try {
        return localStorage.getItem(getExportControlReqAckStorageKey(option)) === '1';
    } catch {
        return false;
    }
};

const parseStoredRequirements = (raw: string | null): ICustomRequirements[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const AdditionalRequirementsSelector: React.FC<AdditionalRequirementsSelectorProps> = ({
    title,
    requirements,
    defaultSelectedIds,
    onChange,
    allowExpediteFilter = true,
    expandLabel = 'Configure requirements',
    expandSubLabel = 'Select additional options...',
    customRequirements,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [expediteOnly, setExpediteOnly] = useState(false);
    const [displayRequirements, setDisplayRequirements] = useState<ICustomRequirements[]>(
        () => customRequirements ?? []
    );
    const [exportControlModalOpen, setExportControlModalOpen] = useState(false);
    const [pendingExportControl, setPendingExportControl] = useState<IRequirement | null>(null);
    const [exportControlAcknowledged, setExportControlAcknowledged] = useState(false);
    const [rememberExportControlAck, setRememberExportControlAck] = useState(false);

    const filteredRequirementsGrouped = useMemo(() => {
        let filtered = requirements;

        if (expediteOnly) {
            filtered = filtered.filter(req => req.safe_to_quote_in?.includes('fastlane'));
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(req => req.name.toLowerCase().includes(query));
        }

        return _.groupBy(filtered, 'type');
    }, [requirements, expediteOnly, searchQuery]);

    const sortedCategories = useMemo(() => {
        return Object.keys(filteredRequirementsGrouped).sort((a, b) => a.localeCompare(b));
    }, [filteredRequirementsGrouped]);

    const applyRequirementToggle = useCallback(
        (option: IRequirement) => {
            const id = option.id;
            const currentIds = new Set(
                displayRequirements.map(cr => Number(cr.key)).filter(keyId => !Number.isNaN(keyId))
            );
            const newSet = new Set(currentIds);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            const prevByKey = new Map(displayRequirements.map(cr => [cr.key, cr]));
            setSearchQuery('');
            const newList = Array.from(newSet).map(selectedId => {
                const key = selectedId.toString();
                const requirement = selectedId === id ? option : requirements.find(r => r.id === selectedId);
                const prev = prevByKey.get(key);
                return {
                    key,
                    label: requirement?.name ?? prev?.label ?? key,
                    value: prev != null ? prev.value : '',
                    files: prev?.files ?? [],
                };
            });
            setDisplayRequirements(newList);

            onChange(newList);
        },
        [displayRequirements, onChange, requirements]
    );

    const handleToggle = (option: IRequirement) => {
        const id = option.id;
        const currentIds = new Set(displayRequirements.map(cr => Number(cr.key)).filter(keyId => !Number.isNaN(keyId)));
        const isRemoving = currentIds.has(id);

        if (!isRemoving && isExportControlRequirement(option) && !shouldSkipExportControlModal(option)) {
            setPendingExportControl(option);
            setExportControlAcknowledged(false);
            setRememberExportControlAck(false);
            setExportControlModalOpen(true);
            return;
        }

        applyRequirementToggle(option);
    };

    const handleExportControlModalCancel = () => {
        setExportControlModalOpen(false);
        setPendingExportControl(null);
    };

    const handleExportControlModalConfirm = () => {
        if (!pendingExportControl || !exportControlAcknowledged) return;
        try {
            if (rememberExportControlAck) {
                localStorage.setItem(getExportControlReqAckStorageKey(pendingExportControl), '1');
            }
        } catch {
            // ignore quota / private mode
        }
        const toAdd = pendingExportControl;
        setExportControlModalOpen(false);
        setPendingExportControl(null);
        applyRequirementToggle(toAdd);
    };

    const handleOnChange = (values: Array<ICustomRequirements>) => {
        setDisplayRequirements(values);
        onChange(values);
    };

    // Debounced function to open first panel when user finishes typing
    const openFirstPanel = useMemo(
        () =>
            _.debounce(() => {
                if (sortedCategories.length > 0) {
                    setSelectedCategory(sortedCategories[0]);
                }
            }, 500),
        [sortedCategories]
    );

    useEffect(() => {
        if (searchQuery.trim()) {
            openFirstPanel();
        }
        return () => {
            openFirstPanel.cancel();
        };
    }, [searchQuery, openFirstPanel]);

    useEffect(() => {
        setDisplayRequirements(customRequirements ?? []);
    }, [customRequirements]);

    return (
        <div className="space-y-2">
            {/*<div className="flex items-center justify-between">*/}
            {/*    <span className="text-slate-600 flex items-center">*/}
            {/*        /!* <FileTextOutlined className="w-4 h-4" /> *!/*/}
            {/*        <span>{title}</span>*/}
            {/*        <QuestionMark>*/}
            {/*            Please select any additional requirements like material certificate, First Article Inspection,*/}
            {/*            Hardness Testing, etc. that you would like to be included with this quote.*/}
            {/*        </QuestionMark>*/}
            {/*    </span>*/}
            {/*    {displayRequirements.length > 0 && (*/}
            {/*        <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full">*/}
            {/*            {displayRequirements.length} selected*/}
            {/*        </span>*/}
            {/*    )}*/}
            {/*</div>*/}
            <Collapse
                bordered={false}
                ghost
                activeKey={expanded ? ['requirements'] : []}
                onChange={keys => setExpanded(Array.isArray(keys) ? keys.length > 0 : !!keys)}
                className="bg-slate-50 rounded-lg"
            >
                <Panel
                    key="requirements"
                    header={
                        <div className="flex w-full items-end align-bottom justify-between">
                            <div className={'flex items-center'}>
                                <span className="text-sm font-medium text-slate-800 ">{expandLabel}</span>
                                <QuestionMark>
                                    Please select any additional requirements like material certificate, First Article
                                    Inspection, Hardness Testing, etc. that you would like to be included with this
                                    quote.
                                </QuestionMark>
                            </div>
                            <div>
                                {expandSubLabel && (
                                    <span className="text-[11px] text-slate-500 whitespace-nowrap">
                                        {expandSubLabel}
                                    </span>
                                )}
                            </div>
                        </div>
                    }
                >
                    <AnimatePresence>
                        {expanded && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="space-y-3 overflow-hidden"
                            >
                                <div className="flex items-center space-x-2">
                                    <div className="relative flex-1">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            placeholder="Search requirements..."
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            className="pl-9 pr-9"
                                        />
                                        {searchQuery && (
                                            <button
                                                onClick={() => setSearchQuery('')}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                    {allowExpediteFilter && (
                                        <div className="flex items-center space-x-2 whitespace-nowrap">
                                            <Switch
                                                checked={expediteOnly}
                                                onChange={setExpediteOnly}
                                                id="expedite-only"
                                            />
                                            <label
                                                htmlFor="expedite-only"
                                                className="text-sm text-slate-700 cursor-pointer"
                                            >
                                                Expedite Only
                                            </label>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    <Collapse
                                        accordion
                                        bordered={false}
                                        ghost
                                        activeKey={selectedCategory || undefined}
                                        onChange={key =>
                                            setSelectedCategory(
                                                Array.isArray(key) ? (key[0] as string) : (key as string) || null
                                            )
                                        }
                                        className="bg-transparent"
                                    >
                                        {sortedCategories.map(category => (
                                            <Panel
                                                key={category}
                                                header={
                                                    <span className="text-sm text-slate-700 font-medium">
                                                        {category}
                                                    </span>
                                                }
                                            >
                                                <div className="flex flex-col space-y-2 items-start">
                                                    {filteredRequirementsGrouped[category]?.map(option => (
                                                        <div key={option.id} className="w-full">
                                                            <Checkbox
                                                                checked={displayRequirements.some(
                                                                    cr => Number(cr.key) === option.id
                                                                )}
                                                                onChange={() => handleToggle(option)}
                                                                className="ml-0"
                                                            >
                                                                {option.name}
                                                            </Checkbox>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Panel>
                                        ))}
                                    </Collapse>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Panel>
            </Collapse>

            {displayRequirements.length > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs font-medium text-blue-900 mb-2">Selected Requirements:</p>
                    <div className="flex flex-wrap gap-1">
                        {displayRequirements.map(cr => {
                            const opt = requirements.find(r => r.id === Number(cr.key));
                            if (!opt) return null;
                            return (
                                <span
                                    key={cr.key}
                                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full flex items-center space-x-1"
                                >
                                    <span>{cr.label || opt.name}</span>
                                    <button
                                        onClick={() => handleToggle(opt)}
                                        className="hover:bg-blue-700 rounded-full flex items-center justify-center"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
            <RenderExtraInputs
                customRequirements={displayRequirements}
                requirements={requirements}
                handleChange={handleOnChange}
            />

            <Modal
                title="Export control documentation"
                open={exportControlModalOpen}
                onCancel={handleExportControlModalCancel}
                destroyOnClose
                maskClosable={false}
                footer={[
                    <Button key="cancel" onClick={handleExportControlModalCancel}>
                        Cancel
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        disabled={!exportControlAcknowledged}
                        onClick={handleExportControlModalConfirm}
                    >
                        Confirm selection
                    </Button>,
                ]}
            >
                <div className="space-y-4 text-slate-700 text-sm">
                    <p>
                        Selecting <strong>{pendingExportControl?.name ?? 'this requirement'}</strong> indicates export
                        control applies to this request. PartsBadger must have appropriate supporting documentation (for
                        example, license or exemption details, jurisdiction classification, or other compliance records
                        your program requires) on file or forthcoming before design work can proceed.
                    </p>
                    <div className="flex flex-col gap-3 w-full [&_.ant-checkbox-wrapper]:ml-0">
                        <Checkbox
                            checked={exportControlAcknowledged}
                            onChange={e => setExportControlAcknowledged(e.target.checked)}
                            className="flex w-full items-start !ml-0"
                        >
                            I confirm that required documentation is provided or will be provided.
                        </Checkbox>
                        <Checkbox
                            checked={rememberExportControlAck}
                            onChange={e => setRememberExportControlAck(e.target.checked)}
                            className="flex w-full items-start !ml-0"
                        >
                            Remember my choice on this browser (skip this message next time)
                        </Checkbox>
                    </div>
                </div>
            </Modal>
        </div>
    );
};